import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import PennText from "@/components/ui/text/PennText";
import { PennButton } from "@/components/ui/button/PennButton";
import { PennCheckbox, Skeleton } from "@/components/ui";
import type { RegionCode, SelectorProduct } from "@/api/filter";
import type { ApplicationSummary, ApplicationDetail } from "@/api/applications";
import type { MachineCostMode, PerAppCostBreakdown } from "@/api/calculate";
import {
  useComparisonCosts,
  type ProductCostState,
} from "@/hooks/useComparisonCosts";
import { useOptimizeAllocation } from "@/hooks/useOptimizeAllocation";
import {
  useDownloadCostAnalysis,
  useDownloadRoiReport,
} from "@/hooks/useQuoteDocuments";
import type { RoiMachineAssignment } from "@/api/quoteDocuments";
import { useAppSelector } from "@/stores/hooks";
import { toDocApplications, toDocAssumptions } from "@/utils/rfq-snapshot";
import {
  formatMoney,
  parseDecimalString,
  pickModeCost,
  roundUtilRatioToDisplayPercent,
} from "@/utils/cost-display";
import { baseApplicationIdFromScoped } from "@/utils/tseries-split";
import { isMspAutoFeedToggleSupported } from "@/utils/mspe-product";
import { mspModelConstantOverrides } from "@/utils/mspe-override-signature";
import { computeGlobalUsefulLifeTotals } from "@/utils/comparison-global-totals";
import { buildConfiguredInvestmentByGroupKey } from "@/utils/build-configure-override-by-group";
import { resolveConfiguredMachinePriceOverride } from "@/utils/configured-investment";
import {
  loadProductSelection,
  manualLineItemsTotalForCurrency,
} from "@/utils/configure-storage";
import type { ModelConstantOverrides } from "@/api/calculate";
import { SelectedAppsTotalsFooter } from "./SelectedAppsTotalsFooter";
import { ComparisonGlobalTotals } from "./ComparisonGlobalTotals";
import { getApiErrorMessage } from "@penn/web-common";
import {
  isRequestAbortError,
  toCalculationRateLimitError,
} from "@/utils/calculation-request-control";

/** POC main content width — max-w-[1600px] mx-auto */
const PAGE_CONTENT_CLASS =
  "w-full max-w-[1600px] mx-auto px-3 sm:px-4 lg:px-6 2xl:px-8";

type AllocationMode = "100%" | "dedicated";

/**
 * POC parity: the machine-cost allocation mode
 * is persisted in localStorage under `machine_cost_mode` (values "dedicated" |
 * "shared") and restored on load so a page refresh keeps the user's last-used
 * mode. Default is "dedicated".
 */
const ALLOCATION_STORAGE_KEY = "machine_cost_mode";

const loadStoredAllocation = (): AllocationMode => {
  try {
    const stored = localStorage.getItem(ALLOCATION_STORAGE_KEY);
    if (stored === "shared") return "100%";
    if (stored === "dedicated") return "dedicated";
  } catch {
    // localStorage unavailable (private mode) — fall back to default.
  }
  return "dedicated";
};

export type ConfigureOpenExtras = {
  /** POC `requiredMachines` from comparison cost preview. */
  requiredMachines: number;
};

type ComparisonListProps = {
  /**
   * Configure modal entry — POC `data-id` parity. Always receives a real
   * `productId` (Q6 — name-only opens are disabled at the FilterPage level).
   */
  onConfigure?: (productId: string, extras: ConfigureOpenExtras) => void;
  onRemoveProduct?: (productId: string) => void;
  products?: SelectorProduct[];
  selected?: string[];
  selectedApps?: ApplicationSummary[];
  /** Full app details (with fasteners) for document downloads. */
  selectedAppDetails?: ApplicationDetail[];
  region: RegionCode;
  isAuthenticated: boolean;
  canShowPrices: boolean;
  /**
   * STRICT price permission (Lars matrix: canSeePrices / canGenerateRoi / admin).
   * Gates the "Totals (N years)" cost rollup — external customers and
   * distributors can run Optimize Allocation but must NOT see the priced totals
   * box (POC `canShowPrices() || canCreateQuotation()`). Distinct from
   * `canShowPrices` above, which is the loose card-price gate (any signed-in user).
   */
  canShowGlobalTotals?: boolean;
  canGenerateQuote?: boolean;
  canGenerateRoi?: boolean;
  /**
   * Controlled state — lifted to FilterPage 2026-05-31 so the configure
   * modal can derive `applicationIds` (Q1) and the MSPe toggle (D9) at open
   * time. ComparisonList no longer owns this state internally.
   */
  selectedMachineByApp: Record<string, string>;
  onToggleMachineForApp: (
    applicationId: string,
    productId: string,
    checked: boolean,
  ) => void;
  mspAutoFeedByProductId: Record<string, boolean>;
  onMspAutoFeedToggle: (productId: string) => void;
  /** Q-OPT-02 — comparison workspace UUID (warms optimize cache + optimize run). */
  optimizeSessionId?: string;
  /** Apply the optimized application→machine assignment (Q-OPT-03 D result). */
  onApplyOptimizedAssignment?: (assignment: Record<string, string>) => void;
  /**
   * Live `requiredMachines` per product from the cost preview. Lets the open
   * Configure modal track utilization changes instead of freezing the value at
   * modal-open time (parity cross-cutting fix). Keyed by productId, ceil'd ≥ 1.
   */
  onRequiredMachinesByProduct?: (map: Record<string, number>) => void;
  /**
   * Monotonic counter bumped by the parent each time the Configure Solution
   * popup is closed after a real change. Re-runs Optimize Allocation so the
   * allocation reflects the just-saved options — but ONLY once optimization is
   * already in play (see `hasOptimized`). Starts at 0 (no auto-run on mount).
   */
  reoptimizeToken?: number;
};

export const ComparisonList = ({
  onConfigure,
  onRemoveProduct,
  products = [],
  selected = [],
  selectedApps = [],
  region,
  isAuthenticated,
  canShowPrices,
  canShowGlobalTotals = false,
  canGenerateQuote = false,
  canGenerateRoi = false,
  selectedMachineByApp,
  onToggleMachineForApp,
  mspAutoFeedByProductId,
  onMspAutoFeedToggle,
  optimizeSessionId,
  onApplyOptimizedAssignment,
  onRequiredMachinesByProduct,
  reoptimizeToken = 0,
}: ComparisonListProps) => {
  // POC parity: initial state machineCostMode: "dedicated",
  // restored from localStorage when present so a refresh keeps the last choice.
  const [allocation, setAllocation] = useState<AllocationMode>(loadStoredAllocation);

  const machineCostMode: MachineCostMode =
    allocation === "100%" ? "shared" : "dedicated";

  // POC parity: persist the allocation choice ("dedicated" | "shared") on every
  // change so the next page load auto-selects it.
  useEffect(() => {
    try {
      localStorage.setItem(ALLOCATION_STORAGE_KEY, machineCostMode);
    } catch {
      // ignore write failures (private mode / storage full)
    }
  }, [machineCostMode]);

  const selectedProducts = useMemo(
    () => products.filter((p) => selected.includes(p.productId)),
    [products, selected],
  );

  const calcEnabled = isAuthenticated && canShowPrices;

  const {
    costByProductId,
    appsValid,
    appDetailsError,
    calculationRetryMessage,
    compareApps,
    splitEnabled,
  } = useComparisonCosts({
    region,
    products,
    selectedProductIds: selected,
    applicationSummaries: selectedApps,
    selectedMachineByApp,
    machineCostMode,
    mspAutoFeedByProductId,
    optimizeSessionId,
    enabled: calcEnabled,
  });

  // Surface live `requiredMachines` (ceil ≥ 1) per product so the Configure
  // modal can re-scale the machine line when utilization changes mid-session.
  useEffect(() => {
    if (!onRequiredMachinesByProduct) return;
    const map: Record<string, number> = {};
    for (const [productId, cost] of Object.entries(costByProductId)) {
      if (cost?.status === "ok") {
        map[productId] =
          cost.assignmentRequiredMachines ??
          Math.max(
            1,
            Math.ceil(Number(cost.data.summary.requiredMachines) || 1),
          );
      }
    }
    onRequiredMachinesByProduct(map);
  }, [costByProductId, onRequiredMachinesByProduct]);

  // POC parity (issue 2): the comparison KPI cards iterate the *derived* apps —
  // the A/B split when a T-Series press is selected, otherwise the base apps.
  // `ApplicationDetail` extends `ApplicationSummary`, so split apps drop in for
  // the per-app card props. Persisted-application reads still use `selectedApps`;
  // allocation-unit flows use the scoped IDs in `compareAppRefs` (ADR-0025).
  const compareAppRefs: ApplicationSummary[] = splitEnabled
    ? compareApps
    : selectedApps;
  const multiApps = compareAppRefs.length > 1;

  // ── B5 (POC parity: bindCompareScrollSync + the
  // _preserveCompareScroll helpers around the allocation toggle) ──
  // (1) Keep the per-card "applications" scroll lists in lock-step so the rows
  // line up across every comparison card while the user scrolls one of them.
  // (2) Preserve that scroll offset across an allocation-toggle re-render — the
  // cards refetch costs and re-mount their scroll lists, which would
  // otherwise snap the user back to the top.
  const cardsGridRef = useRef<HTMLDivElement | null>(null);
  const compareScrollTopRef = useRef(0);
  const pendingScrollRestoreRef = useRef<number | null>(null);

  useEffect(() => {
    const grid = cardsGridRef.current;
    if (!grid) return;
    const lists = Array.from(
      grid.querySelectorAll<HTMLElement>(".compare-apps-scroll"),
    );
    if (lists.length < 2) return;
    let syncing = false;
    const onScroll = (e: Event) => {
      if (syncing) return;
      const target = e.target as HTMLElement;
      syncing = true;
      compareScrollTopRef.current = target.scrollTop;
      for (const el of lists) if (el !== target) el.scrollTop = target.scrollTop;
      syncing = false;
    };
    for (const el of lists)
      el.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      for (const el of lists) el.removeEventListener("scroll", onScroll);
    };
  }, [multiApps, selectedProducts, compareAppRefs, costByProductId]);

  useLayoutEffect(() => {
    const target = pendingScrollRestoreRef.current;
    if (target == null) return;
    const grid = cardsGridRef.current;
    if (!grid) return;
    const lists = Array.from(
      grid.querySelectorAll<HTMLElement>(".compare-apps-scroll"),
    );
    // Lists not mounted yet (cards still showing the loading skeleton) — leave
    // the pending value in place and restore on the next render.
    if (lists.length === 0) return;
    for (const el of lists) el.scrollTop = target;
    pendingScrollRestoreRef.current = null;
  }, [costByProductId, machineCostMode]);

  const setAllocationPreservingScroll = (mode: AllocationMode) => {
    const first = cardsGridRef.current?.querySelector<HTMLElement>(
      ".compare-apps-scroll",
    );
    pendingScrollRestoreRef.current = first
      ? first.scrollTop
      : compareScrollTopRef.current;
    setAllocation(mode);
  };

  const { lowestInvestId, lowestInstallId, lowestInstallByAppId } = useMemo(() => {
    let lowestInvestId: string | null = null;
    let lowestInstallId: string | null = null;
    const lowestInstallByAppId: Record<string, string> = {};

    if (!calcEnabled || !appsValid) {
      return { lowestInvestId, lowestInstallId, lowestInstallByAppId };
    }

    if (!multiApps) {
      let minInvest: number | null = null;
      let minInstall: number | null = null;
      for (const p of selectedProducts) {
        const cost = costByProductId[p.productId];
        if (cost?.status !== "ok") continue;
        const inv = parseDecimalString(cost.data.aggregate.investmentCost);
        const installStr = pickModeCost(machineCostMode, {
          shared: cost.data.summary.costPerPartShared,
          dedicated: cost.data.summary.costPerPartDedicated,
          fallback: cost.data.summary.costPerPart,
        });
        const install = parseDecimalString(installStr);
        if (inv != null && (minInvest == null || inv < minInvest)) {
          minInvest = inv;
          lowestInvestId = p.productId;
        }
        if (install != null && (minInstall == null || install < minInstall)) {
          minInstall = install;
          lowestInstallId = p.productId;
        }
      }
      return { lowestInvestId, lowestInstallId, lowestInstallByAppId };
    }

    // B6 (POC parity): the global lowest-investment
    // machine is also needed in multi-app mode so each per-app block can show
    // the "lowest invest" highlight alongside the per-app "lowest install".
    let minInvest: number | null = null;
    for (const p of selectedProducts) {
      const cost = costByProductId[p.productId];
      if (cost?.status !== "ok") continue;
      const inv = parseDecimalString(cost.data.aggregate.investmentCost);
      if (inv != null && (minInvest == null || inv < minInvest)) {
        minInvest = inv;
        lowestInvestId = p.productId;
      }
    }

    for (const app of compareAppRefs) {
      let min: number | null = null;
      let winner: string | null = null;
      for (const p of selectedProducts) {
        const cost = costByProductId[p.productId];
        if (cost?.status !== "ok") continue;
        const row = cost.data.perApp.find((r) => r.applicationId === app.applicationId);
        if (!row) continue;
        const installStr = pickModeCost(machineCostMode, {
          shared: row.costPerPartShared,
          dedicated: row.costPerPartDedicated,
          fallback: row.costPerPart,
        });
        const install = parseDecimalString(installStr);
        if (install != null && (min == null || install < min)) {
          min = install;
          winner = p.productId;
        }
      }
      if (winner) lowestInstallByAppId[app.applicationId] = winner;
    }

    return { lowestInvestId, lowestInstallId, lowestInstallByAppId };
  }, [
    calcEnabled,
    appsValid,
    multiApps,
    selectedProducts,
    compareAppRefs,
    costByProductId,
    machineCostMode,
  ]);

  const showReportActions =
    (canGenerateQuote || canGenerateRoi) && selectedProducts.length > 0;
  // POC parity: BOTH report buttons enable on
  // `canCreateQuotation() && selected.length>0 && inputsValid && appsAvailable`
  // — there is NO "all applications assigned to a machine" requirement on the
  // button. Cost Analysis downloads with a single application.
  const reportActionsEnabled =
    showReportActions && appsValid && selectedApps.length > 0;
  // One application always auto-assigns, at any machine count (product decision
  // 2026-09-07) — the user should never have to tick a box to reach ROI when there
  // is only one thing to report on. With several machines compared the pick is the
  // cheapest-to-install one (the card already badged LOWEST INSTALLATION COST), so
  // the report shows the best case rather than a selection-order accident. Falls
  // back to the first machine only while costs are still resolving, since
  // `lowestInstall*` is null until then. An explicit tick still wins: the
  // checkboxes stay rendered and `selectedMachineByApp` overrides this below.
  // A single two-sided part under T-Series split is ONE application presented as
  // two allocation units ("<app> (A)" + "<app> (B)"), so counting
  // `compareAppRefs` would see 2 and skip auto-assign — the user then had to tick
  // a box for a report on their only application. Count DISTINCT BASE
  // applications instead, and auto-assign every side of that lone application.
  const soleAppIds = (() => {
    const baseIds = new Set(
      compareAppRefs.map((a) => baseApplicationIdFromScoped(a.applicationId)),
    );
    if (baseIds.size !== 1) return [];
    // An explicit tick on ANY unit wins outright: ticking only side (A) must
    // report on (A) alone, so auto-assign fires only when nothing is ticked.
    const hasExplicitTick = compareAppRefs.some(
      (a) => selectedMachineByApp[a.applicationId] != null,
    );
    return hasExplicitTick ? [] : compareAppRefs.map((a) => a.applicationId);
  })();
  const autoAssignedByApp: Record<string, string> = {};
  for (const appId of soleAppIds) {
    const productId =
      lowestInstallByAppId[appId] ??
      lowestInstallId ??
      selectedProducts[0]?.productId ??
      null;
    if (productId) autoAssignedByApp[appId] = productId;
  }
  const roiMachineByApp: Record<string, string> = {
    ...autoAssignedByApp,
    ...selectedMachineByApp,
  };
  // ROI generates for the ticked subset: one unassigned application must not block
  // the report (product decision 2026-09-07). Still requires >= 1 valid assignment,
  // and still ignores assignments pointing at a since-removed product.
  const assignedAppRefs = compareAppRefs.filter((a) => {
    const pid = roiMachineByApp[a.applicationId];
    return pid != null && selectedProducts.some((p) => p.productId === pid);
  });
  const hasRoiAssignment = assignedAppRefs.length > 0;
  // A lone application still gets a checkbox per machine card so the user can
  // override the auto-assigned pick. Only the per-app list widens — global winner
  // tabs stay on `multiApps`.
  const showPerAppPicker = multiApps || selectedProducts.length > 1;

  // Ad-hoc document downloads (no task) — compare view. Both Cost Analysis and
  // ROI are gated by canGenerateRoi (backend parity; #5 fix). machineTypes are
  // the selected productIds (schema caps at 4).
  const assumptions = useAppSelector((s) => s.auth.assumptions);
  const downloadCostAnalysis = useDownloadCostAnalysis();
  const downloadRoiReport = useDownloadRoiReport();
  const docBusy = downloadCostAnalysis.isPending || downloadRoiReport.isPending;
  const docError = downloadCostAnalysis.isError || downloadRoiReport.isError;
  // POC-parity inline message for the ROI assignment guard.
  const [roiMessage, setRoiMessage] = useState<string | null>(null);
  // The guard message is otherwise only cleared by a later successful click, so it
  // reads as "stuck" after the user ticks a box and satisfies the guard.
  useEffect(() => {
    if (hasRoiAssignment) setRoiMessage(null);
  }, [hasRoiAssignment]);
  const reportPreparationControllerRef = useRef<AbortController | null>(null);

  // Product-scoped manual rows are part of the displayed one-time investment
  // and therefore part of Optimize's objective. Read them during render so a
  // Configure-close parent update observes the just-persisted workspace draft.
  const manualLineTotalByProductId: Record<string, number> = {};
  for (const product of selectedProducts) {
    const cost = costByProductId[product.productId];
    if (!cost || cost.status !== "ok") continue;
    const stored = loadProductSelection(product.productId);
    manualLineTotalByProductId[product.productId] =
      manualLineItemsTotalForCurrency(
        stored?.manualLineItems,
        cost.data.currencyCode,
      );
  }

  // Global cost-of-ownership rollup shown below the action bar: one-time
  // configured machine investment plus N-year labour and quality. Price-gated (POC
  // `canShowPrices() || canCreateQuotation()` — co-granted with quote in the
  // Lars RBAC matrix) and only when every application is assigned to a machine
  // whose cost preview is ready (POC `allAssigned`/`ok`).
  const globalTotals = (() => {
    // STRICT price gate: hidden from external customers / distributors even
    // though they can run optimize allocation (they lack canSeePrices/quote).
    if (!canShowGlobalTotals || !calcEnabled || !appsValid) return null;
    if (selectedProducts.length === 0 || compareAppRefs.length === 0) return null;

    return computeGlobalUsefulLifeTotals({
      appRefs: compareAppRefs,
      selectedMachineByApp,
      costByProductId,
      manualLineTotalByProductId,
      usefulLifeYears: parseDecimalString(assumptions?.usefulLifeYears) ?? 7,
    });
  })();

  const buildDocBase = (applications: ApplicationDetail[]) => ({
    regionCode: region,
    machineCostMode,
    machineTypes: selectedProducts.map((p) => p.productId).slice(0, 4),
    applications: toDocApplications(applications),
    assumptions: toDocAssumptions(assumptions),
  });

  // POC parity (`buildRoiReportData` L11461-11522): the ROI report
  // is built per ASSIGNED machine. Each machine that has ≥1 application selected
  // on its card is sent with that app set and its configured BOM price (the
  // per-unit `aggregate.investmentCost` the comparison cards already resolved,
  // = svc-10 `machinePriceUsed` after the BOM override). svc-06 then costs each
  // machine with only its apps + this price, drops empty machines, and sums the
  // investment — instead of costing every machine with every app at the catalog
  // average. `applicationId`s match the `applications[]` from `toDocApplications`.
  const buildMachineAssignments = async (
    signal: AbortSignal,
  ): Promise<RoiMachineAssignment[]> => {
    const byProduct = new Map<string, string[]>();
    for (const app of compareAppRefs) {
      const productId = roiMachineByApp[app.applicationId];
      if (!productId) continue;
      if (!selectedProducts.some((p) => p.productId === productId)) continue;
      const list = byProduct.get(productId) ?? [];
      list.push(app.applicationId);
      byProduct.set(productId, list);
    }
    const out: RoiMachineAssignment[] = [];
    for (const [productId, applicationIds] of byProduct) {
      if (signal.aborted) {
        throw signal.reason ?? new DOMException("Aborted", "AbortError");
      }
      const machinePriceOverride = await resolveConfiguredMachinePriceOverride({
        productId,
        region,
        applicationIds,
        mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
        requiredMachines: 1,
        signal,
      });
      out.push({
        productId,
        applicationIds,
        ...(machinePriceOverride ? { machinePriceOverride } : {}),
      });
    }
    return out;
  };

  // CR-006: per-machineType configured BOM price so Cost Analysis reflects a
  // manually-configured solution (same manual-aware override the cards + ROI use).
  const buildCostMachineOverrides = async (
    signal: AbortSignal,
  ): Promise<Record<string, string>> => {
    const out: Record<string, string> = {};
    const machineTypes = selectedProducts.map((p) => p.productId).slice(0, 4);
    const applicationIds = compareAppRefs.map((a) => a.applicationId);
    // Resolve sequentially so an HTTP 429 stops the queue before another
    // product can consume rate-limit capacity. The signal also cancels this
    // preparation when a newer download starts or the component unmounts.
    for (const productId of machineTypes) {
      if (signal.aborted) {
        throw signal.reason ?? new DOMException("Aborted", "AbortError");
      }
      const override = await resolveConfiguredMachinePriceOverride({
        productId,
        region,
        applicationIds,
        mspAutoFeedEnabled: mspAutoFeedByProductId[productId] ?? true,
        requiredMachines: 1,
        signal,
      });
      if (override) out[productId] = override;
    }
    return out;
  };

  const handleDownloadCostAnalysis = () => {
    if (!reportActionsEnabled || docBusy) return;
    // Cost Analysis has no assignment requirement (POC parity) — works with one app.
    // §D.5 / MD-07: the POC downloads the same T-Series-derived A/B application
    // list used by the comparison cards, so each side is calculated separately.
    setRoiMessage(null);
    reportPreparationControllerRef.current?.abort();
    const controller = new AbortController();
    reportPreparationControllerRef.current = controller;
    void (async () => {
      try {
        const machinePriceOverrideByMachineType =
          await buildCostMachineOverrides(controller.signal);
        if (controller.signal.aborted) return;
        downloadCostAnalysis.mutate({
          adHoc: {
            ...buildDocBase(compareApps),
            ...(Object.keys(machinePriceOverrideByMachineType).length > 0
              ? { machinePriceOverrideByMachineType }
              : {}),
          },
        });
      } catch (error) {
        if (isRequestAbortError(error)) return;
        setRoiMessage(
          toCalculationRateLimitError(error)?.message ??
            getApiErrorMessage(error, "Could not prepare Cost Analysis."),
        );
      } finally {
        if (reportPreparationControllerRef.current === controller) {
          reportPreparationControllerRef.current = null;
        }
      }
    })();
  };

  const handleGenerateRoi = () => {
    if (!reportActionsEnabled || docBusy) return;
    // ROI needs at least one application: the backend's `assertHasApplications`
    // requires >= 1, and the comparison baseline is a manual 618Pro machine, not
    // a second application — so savings, chart and payback all exist at n=1.
    // The stale-assignment case (a prior Optimize leaving an assignment for a
    // since-removed app) is owned by `hasRoiAssignment` below, not by this count.
    if (compareAppRefs.length < 1) {
      setRoiMessage(
        "Add at least one application to a machine before generating the report.",
      );
      return;
    }
    if (!hasRoiAssignment) {
      setRoiMessage(
        "Select at least one application on a machine before generating the report.",
      );
      return;
    }
    setRoiMessage(null);
    reportPreparationControllerRef.current?.abort();
    const controller = new AbortController();
    reportPreparationControllerRef.current = controller;
    void (async () => {
      try {
        const machineAssignments = await buildMachineAssignments(
          controller.signal,
        );
        if (controller.signal.aborted) return;
        // svc-06 costs the 618Pro baseline over EVERY application in
        // `applications[]` while the configured side is grouped by
        // `machineAssignments`. Send only the assigned applications so both sides
        // cover the same production — otherwise a partial assignment compares 1
        // app against N and inflates savings.
        const assignedIds = new Set(
          assignedAppRefs.map((a) => a.applicationId),
        );
        const roiApps = compareApps.filter((a) =>
          assignedIds.has(a.applicationId),
        );
        downloadRoiReport.mutate({
          adHoc: {
            ...buildDocBase(roiApps),
            baselineMachineType: null,
            machineAssignments,
          },
        });
      } catch (error) {
        if (isRequestAbortError(error)) return;
        setRoiMessage(
          toCalculationRateLimitError(error)?.message ??
            getApiErrorMessage(error, "Could not prepare ROI report."),
        );
      } finally {
        if (reportPreparationControllerRef.current === controller) {
          reportPreparationControllerRef.current = null;
        }
      }
    })();
  };

  // Optimize Allocation (Q-OPT-03 D). POC parity:
  // `showOpt = appsAvailable && selected.length > 0 && apps.length > 1` — the
  // `apps` list here is the derived comparison list, so T-Series A/B sides are
  // independent allocation units. B3 (POC parity): dropped the MVP-only extra
  // "≥2 machines" condition that hid the valid 1-machine / 2-app case.
  const showOptimize =
    selectedProducts.length >= 1 && compareAppRefs.length >= 2;
  // #3 — POC does NOT require every machine to be priceable before optimizing.
  // Its optimizer (and our svc-10 worker, optimize-allocation.service.ts:340)
  // simply skips application→machine combos that have no cost model
  // (infeasible machines score `null`) and ranks the feasible rest. Gating on
  // "all machines costed" was an MVP-only over-restriction that disabled the
  // button whenever a single machine/app combo couldn't be priced.
  const optimizeEnabled =
    calcEnabled &&
    canShowPrices &&
    appsValid &&
    compareAppRefs.length >= 2 &&
    selectedProducts.length >= 1 &&
    !!optimizeSessionId;

  const optimize = useOptimizeAllocation();
  const [optimizeModalOpen, setOptimizeModalOpen] = useState(false);
  const optimizePreparationControllerRef = useRef<AbortController | null>(
    null,
  );
  const optimizeRetryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const optimizeRetryAtRef = useRef(0);
  const [optimizeRateLimited, setOptimizeRateLimited] = useState(false);
  // Whether Optimize Allocation has run to completion at least once this
  // session. Auto re-optimize on Configure-close only kicks in after this, so a
  // user who is only configuring never has the solver silently reallocate.
  const [hasOptimized, setHasOptimized] = useState(false);
  // CR-006 (Finding 1): Optimize must not run until every selected product has a
  // completed calculation. Editing Configure invalidates + clears
  // `costByProductId`, and Configure-close immediately bumps the re-optimize
  // token; without this gate the solver would prepare authoritative investments
  // from missing utilisation (silently required-machines = 1) while the footer
  // later computes 2/3 — re-introducing the objective mismatch.
  const optimizeCostsReady =
    selectedProducts.length > 0 &&
    selectedProducts.every((p) => costByProductId[p.productId]?.status === "ok");
  // A re-optimize (or a too-early manual run) requested before the calcs are
  // ready is deferred until `optimizeCostsReady` flips true.
  const reoptimizePendingRef = useRef(false);

  useEffect(
    () => () => {
      reportPreparationControllerRef.current?.abort();
      optimizePreparationControllerRef.current?.abort();
      if (optimizeRetryTimerRef.current) {
        clearTimeout(optimizeRetryTimerRef.current);
      }
    },
    [],
  );

  const handleOptimize = () => {
    if (!optimizeEnabled || !optimizeSessionId || optimize.isRateLimited) return;
    if (Date.now() < optimizeRetryAtRef.current) return;
    if (!optimizeCostsReady) {
      reoptimizePendingRef.current = true;
      return;
    }
    reoptimizePendingRef.current = false;
    const machineIds = selectedProducts.map((p) => p.productId);
    const applicationIds = compareAppRefs.map((a) => a.applicationId);
    const modelConstantOverridesByProductId: Record<
      string,
      ModelConstantOverrides
    > = {};
    for (const p of selectedProducts) {
      const o = mspModelConstantOverrides(
        p.productId,
        mspAutoFeedByProductId[p.productId] ?? true,
      );
      if (o) modelConstantOverridesByProductId[p.productId] = o;
    }
    // CR-006: per-app machine utilisation from the current comparison calc, used
    // to derive each candidate subset's required machines (same basis as the
    // footer's `requiredMachinesForAssignment`).
    const utilByMachineApp: Record<string, Record<string, number>> = {};
    for (const p of selectedProducts) {
      const cost = costByProductId[p.productId];
      if (cost?.status !== "ok") continue;
      const byApp: Record<string, number> = {};
      for (const row of cost.data.perApp) {
        if (row.applicationId) {
          const utilization = Number(row.machineUtilization);
          if (Number.isFinite(utilization) && utilization >= 0) {
            byApp[row.applicationId] = utilization;
          }
        }
      }
      utilByMachineApp[p.productId] = byApp;
    }
    optimizePreparationControllerRef.current?.abort();
    const controller = new AbortController();
    optimizePreparationControllerRef.current = controller;
    setOptimizeModalOpen(true);
    optimize.beginPreparing();
    void (async () => {
      try {
        const configuredInvestmentByGroupKey =
          await buildConfiguredInvestmentByGroupKey({
            productIds: machineIds,
            applicationIds,
            region,
            mspAutoFeedByProductId,
            splitEnabled,
            utilByMachineApp,
            manualLineTotalByProductId,
            signal: controller.signal,
          });
        if (controller.signal.aborted) return;
        void optimize.run({
          optimizeSessionId,
          region,
          machineCostMode,
          applicationIds,
          machineIds,
          selectedMachineByApp,
          ...(Object.keys(modelConstantOverridesByProductId).length > 0
            ? { modelConstantOverridesByProductId }
            : {}),
          ...(Object.keys(configuredInvestmentByGroupKey).length > 0
            ? { configuredInvestmentByGroupKey }
            : {}),
        });
      } catch (error) {
        if (isRequestAbortError(error) || controller.signal.aborted) return;
        const rateLimitError = toCalculationRateLimitError(error);
        if (rateLimitError) {
          optimizeRetryAtRef.current = rateLimitError.retryAtMs;
          setOptimizeRateLimited(true);
          if (optimizeRetryTimerRef.current) {
            clearTimeout(optimizeRetryTimerRef.current);
          }
          optimizeRetryTimerRef.current = setTimeout(
            () => {
              optimizeRetryAtRef.current = 0;
              setOptimizeRateLimited(false);
            },
            Math.max(0, rateLimitError.retryAtMs - Date.now()),
          );
          optimize.fail(rateLimitError.message);
          return;
        }
        optimize.fail(
          getApiErrorMessage(error, "Failed to prepare optimization."),
        );
      } finally {
        if (optimizePreparationControllerRef.current === controller) {
          optimizePreparationControllerRef.current = null;
        }
      }
    })();
  };

  // POC parity: apply the winning assignment AND auto-close the modal the moment
  // the job completes (POC shows no "done" state — it just closes). Cancel also
  // closes the modal.
  const optimizeStatus = optimize.state.status;
  useEffect(() => {
    if (optimize.state.status === "completed") {
      const map: Record<string, string> = {};
      for (const a of optimize.state.result.assignment) {
        map[a.applicationId] = a.productId;
      }
      // The assignment itself is part of useComparisonCosts' cache key. Let
      // React commit it before recalculation; synchronously broadcasting an
      // invalidation here raced the state update and warmed the old assignment.
      onApplyOptimizedAssignment?.(map);
      setOptimizeModalOpen(false);
      setHasOptimized(true);
      optimize.reset();
    } else if (optimize.state.status === "cancelled") {
      setOptimizeModalOpen(false);
      optimize.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizeStatus]);

  // Re-run Optimize Allocation when the Configure popup closes after a change,
  // so the allocation reflects the just-saved options.
  useEffect(() => {
    if (reoptimizeToken > 0 && hasOptimized && optimizeEnabled) {
      // Park directly rather than via handleOptimize, which drops the request
      // when rate-limited or inside the retry backoff.
      if (optimizeCostsReady) handleOptimize();
      else reoptimizePendingRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reoptimizeToken]);

  // Finding 1: run a deferred (re)optimization the moment the calcs become
  // ready. Configure-close bumps the token while `costByProductId` is still
  // cleared/loading, so `handleOptimize` parks the request here instead of
  // preparing investments from missing utilisation.
  useEffect(() => {
    if (
      reoptimizePendingRef.current &&
      optimizeCostsReady &&
      optimizeEnabled &&
      !optimize.isRateLimited
    ) {
      handleOptimize();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optimizeCostsReady, optimizeEnabled]);

  const closeOptimizeModal = () => {
    optimizePreparationControllerRef.current?.abort();
    setOptimizeModalOpen(false);
    optimize.reset();
  };

  const cancelOptimize = () => {
    optimizePreparationControllerRef.current?.abort();
    void optimize.cancel();
  };

  return (
    <section id="comparison-list" className="pb-6 scroll-mt-28">
      <div className={`${PAGE_CONTENT_CLASS} mt-8 mb-3 text-center`}>
        <h2 className="text-h2 text-on-navy">
          Comparison list
        </h2>
        <p className="mt-1 text-body-lg text-on-navy-secondary">
          Review selected solutions side-by-side and open configuration to create quotations.
        </p>
      </div>

      <div className={PAGE_CONTENT_CLASS}>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-soft p-5 pt-10">
          {!isAuthenticated && selectedProducts.length > 0 && (
            <PennText as="p" size="sm" color="blue_grey" align="center" className="mb-4">
              Sign in to see installation costs on comparison cards.
            </PennText>
          )}

          {/* money_too (2026-06-22): comparison-card cost + utilization KPIs are
              shown to every authenticated user (full POC parity), so the old
              "Pricing is not available for your account" banner is removed —
              `canShowPrices` is true for all authenticated users here. */}

          {calcEnabled && selectedApps.length === 0 && selectedProducts.length > 0 && (
            <PennText as="p" size="sm" color="blue_grey" align="center" className="mb-4">
              Add application parameters to calculate installation costs.
            </PennText>
          )}

          {calcEnabled && selectedApps.length > 0 && !appsValid && (
            <PennText as="p" size="sm" color="blue_grey" align="center" className="mb-4">
              Enter annual volume, batch size, and fastener quantities on each application to
              calculate costs.
            </PennText>
          )}

          {appDetailsError && (
            <PennText as="p" size="sm" color="blue_grey" align="center" className="mb-4">
              {appDetailsError}
            </PennText>
          )}

          {calculationRetryMessage && (
            <div
              role="alert"
              className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-center text-sm text-amber-900"
            >
              {calculationRetryMessage}
            </div>
          )}

          {selectedProducts.length === 0 ? (
            <div className="text-center py-8 border border-dashed border-gray-200 rounded-2xl px-4">
              <PennText as="p" size="sm" color="blue_grey">
                No solutions selected yet. Check solutions above to compare them here.
              </PennText>
            </div>
          ) : (
            <div
              ref={cardsGridRef}
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
            >
              {selectedProducts.map((p) => {
            const cost = costByProductId[p.productId] ?? { status: "idle" as const };
            const isLowestInvest =
              !multiApps && appsValid && p.productId === lowestInvestId;
            const isLowestInstall =
              !multiApps && appsValid && p.productId === lowestInstallId;
            const isPerAppWinner = multiApps
              ? Object.values(lowestInstallByAppId).includes(p.productId)
              : false;
            const highlightClass =
              !multiApps && (isLowestInvest || isLowestInstall)
                ? isLowestInvest && isLowestInstall
                  ? "compare-card-hl-both"
                  : isLowestInvest
                    ? "compare-card-hl-invest"
                    : "compare-card-hl-install"
                : isPerAppWinner
                  ? "compare-card-hl-install"
                  : "";

            return (
              <MachineCard
                key={p.productId}
                product={p}
                productId={p.productId}
                costState={cost}
                machineCostMode={machineCostMode}
                showPrices={canShowPrices}
                calcEnabled={calcEnabled}
                appsValid={appsValid}
                highlightClass={highlightClass}
                showInvestTab={isLowestInvest}
                showInstallTab={isLowestInstall}
                hideGlobalTabs={multiApps}
                multiApps={showPerAppPicker}
                selectedApps={compareAppRefs}
                lowestInstallByAppId={lowestInstallByAppId}
                lowestInvestId={lowestInvestId}
                selectedMachineByApp={selectedMachineByApp}
                onToggleMachineForApp={(appId, checked) =>
                  onToggleMachineForApp(appId, p.productId, checked)
                }
                mspAutoFeedOn={mspAutoFeedByProductId[p.productId] ?? true}
                onMspAutoFeedToggle={() => onMspAutoFeedToggle(p.productId)}
                onRemove={() => onRemoveProduct?.(p.productId)}
                onConfigure={() => {
                  const cost = costByProductId[p.productId];
                  const requiredMachines =
                    cost?.status === "ok"
                      ? (cost.assignmentRequiredMachines ??
                        Math.max(
                          1,
                          Math.ceil(
                            Number(cost.data.summary.requiredMachines) || 1,
                          ),
                        ))
                      : 1;
                  onConfigure?.(p.productId, { requiredMachines });
                }}
              />
            );
              })}
            </div>
          )}

          {selectedProducts.length > 0 && (
            <div className="mt-4 flex flex-col gap-2">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex flex-wrap items-center justify-center gap-2 w-full md:w-auto">
                  <span className="text-xs font-semibold uppercase text-blue_grey">
                    Machine cost allocation
                  </span>
                  <div className="inline-flex items-center rounded-xl border border-gray-200 bg-white p-0.5 shadow-sm">
                    <button
                      type="button"
                      onClick={() => setAllocationPreservingScroll("100%")}
                      className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                        allocation === "100%"
                          ? "bg-penn-100 text-penn-700"
                          : "text-blue_grey1 hover:bg-gray-50"
                      }`}
                    >
                      100% utilization
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllocationPreservingScroll("dedicated")}
                      className={`h-8 px-3 rounded-lg text-xs font-semibold transition-colors cursor-pointer ${
                        allocation === "dedicated"
                          ? "bg-penn-500 text-white"
                          : "text-blue_grey1 hover:bg-gray-50"
                      }`}
                    >
                      Dedicated to this application
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-center md:justify-end gap-2">
                  {canGenerateRoi && (
                    <PennButton
                      variant="primary"
                      size="sm"
                      className="!h-10 !text-sm !rounded-2xl"
                      disabled={!reportActionsEnabled || docBusy}
                      onClick={handleDownloadCostAnalysis}
                      title={
                        reportActionsEnabled
                          ? undefined
                          : "Enter valid application details to download cost analysis."
                      }
                    >
                      {downloadCostAnalysis.isPending ? "Preparing…" : "Download Cost Analysis"}
                    </PennButton>
                  )}
                  {canGenerateRoi && (
                    <PennButton
                      variant="secondary"
                      size="sm"
                      className="!h-10 !text-sm !rounded-2xl"
                      disabled={!reportActionsEnabled || docBusy}
                      onClick={handleGenerateRoi}
                      title={
                        reportActionsEnabled
                          ? undefined
                          : "Enter valid application details to generate ROI report."
                      }
                    >
                      {downloadRoiReport.isPending ? "Preparing…" : "Generate ROI Report"}
                    </PennButton>
                  )}
                  {showOptimize && (
                    <PennButton
                      variant="secondary"
                      size="sm"
                      className="!h-10 !text-sm !rounded-2xl"
                      disabled={
                        !optimizeEnabled ||
                        !optimizeCostsReady ||
                        optimize.isRunning ||
                        optimize.isRateLimited ||
                        optimizeRateLimited
                      }
                      onClick={handleOptimize}
                      title={
                        optimizeEnabled
                          ? "Find the lowest 7-year total cost application→machine assignment."
                          : "Select at least 2 applications and a machine with valid application details to optimize."
                      }
                    >
                      Optimize Allocation
                    </PennButton>
                  )}
                </div>
              </div>
              {(roiMessage || docError) && (
                <PennText
                  as="p"
                  size="xs"
                  color="blue_grey"
                  align="center"
                  className="md:text-right"
                >
                  {roiMessage ?? "Could not generate the document. Please try again."}
                </PennText>
              )}
            </div>
          )}

          {globalTotals && <ComparisonGlobalTotals totals={globalTotals} />}
        </div>
      </div>

      <OptimizeAllocationModal
        open={optimizeModalOpen}
        onClose={closeOptimizeModal}
        state={optimize.state}
        onCancel={cancelOptimize}
      />
    </section>
  );
};

type OptimizeModalState = ReturnType<typeof useOptimizeAllocation>["state"];

// POC `optimizeModal` parity: a progress-only modal (no "done"
// state — the parent auto-closes on completion). Markup/text/typography mirror
// the POC exactly: title "Optimizing allocation", "ETA --:--", progress
// "Optimizing allocation… X / Y", the wait note, and a single Cancel button.
export function OptimizeAllocationModal({
  open,
  onClose,
  state,
  onCancel,
}: {
  open: boolean;
  onClose: () => void;
  state: OptimizeModalState;
  onCancel: () => void;
}) {
  // ETA estimate (POC parity): remaining ≈ elapsed × (total − processed) / processed.
  const startRef = useRef<number | null>(null);
  const running = state.status === "running";
  useEffect(() => {
    if (running) {
      if (startRef.current === null) startRef.current = Date.now();
    } else {
      startRef.current = null;
    }
  }, [running]);

  if (!open) return null;

  const processed = running ? state.processed : 0;
  const total = running ? state.total : 0;
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;

  let eta = "--:--";
  if (running && processed > 0 && total > 0 && startRef.current !== null) {
    const elapsed = (Date.now() - startRef.current) / 1000;
    const remaining = Math.max(0, Math.round((elapsed * (total - processed)) / processed));
    eta = `${String(Math.floor(remaining / 60)).padStart(2, "0")}:${String(remaining % 60).padStart(2, "0")}`;
  }

  const isError = state.status === "error";
  const retryMessage = running ? state.retryMessage : undefined;
  const retryAutomatically = running ? state.retryAutomatically : undefined;

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="absolute inset-0 flex items-center justify-center p-4">
        <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between gap-3">
            <div className="text-base font-semibold text-slate-900">Optimizing allocation</div>
            <div className="text-xs text-slate-500">ETA {eta}</div>
          </div>

          {isError ? (
            <div className="mt-2 text-sm text-rose-700">{state.message}</div>
          ) : (
            <>
              <div className="mt-2 text-sm text-slate-600">
                {running && total > 0
                  ? `Optimizing allocation… ${processed} / ${total}`
                  : "Optimizing allocation…"}
              </div>
              {retryMessage && (
                <div
                  role="alert"
                  className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900"
                >
                  {retryMessage}
                  {retryAutomatically !== false && (
                    <> Optimization will resume automatically; you can still cancel it.</>
                  )}
                </div>
              )}
              <div className="mt-3 h-3 w-full rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full bg-penn-700 transition-all" style={{ width: `${pct}%` }} />
              </div>
              <div className="mt-2 text-xs text-slate-500">
                Please wait. This can take a few minutes for many combinations.
              </div>
            </>
          )}

          <div className="mt-4 flex items-center justify-end">
            <button
              type="button"
              onClick={isError ? onClose : onCancel}
              className="h-9 px-3 rounded-xl border border-slate-300 bg-white text-sm hover:bg-slate-50"
            >
              {isError ? "Close" : "Cancel"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type MachineCardProps = {
  product: SelectorProduct;
  costState: ProductCostState;
  machineCostMode: MachineCostMode;
  showPrices: boolean;
  calcEnabled: boolean;
  appsValid: boolean;
  highlightClass: string;
  showInvestTab: boolean;
  showInstallTab: boolean;
  hideGlobalTabs: boolean;
  multiApps: boolean;
  selectedApps: ApplicationSummary[];
  lowestInstallByAppId: Record<string, string>;
  /** B6 — global lowest-investment machine id (per-app "lowest invest" highlight). */
  lowestInvestId: string | null;
  selectedMachineByApp: Record<string, string>;
  onToggleMachineForApp: (appId: string, checked: boolean) => void;
  productId: string;
  mspAutoFeedOn: boolean;
  onMspAutoFeedToggle: () => void;
  onRemove?: () => void;
  onConfigure?: () => void;
};

const MachineCard = ({
  product,
  costState,
  machineCostMode,
  showPrices,
  calcEnabled,
  appsValid,
  highlightClass,
  showInvestTab,
  showInstallTab,
  hideGlobalTabs,
  multiApps,
  selectedApps,
  lowestInstallByAppId,
  lowestInvestId,
  selectedMachineByApp,
  onToggleMachineForApp,
  productId,
  mspAutoFeedOn,
  onMspAutoFeedToggle,
  onRemove,
  onConfigure,
}: MachineCardProps) => {
  const showMspToggle = isMspAutoFeedToggleSupported(product.productId);
  const calcOk = costState.status === "ok";
  const currency = calcOk ? costState.data.currencyCode : undefined;
  const summary = calcOk ? costState.data.summary : null;

  // Cost model unavailable / price unavailable: per MD-04 the MVP does NOT
  // invent synthetic ("greyed-out calculation") numbers or fall back to machine
  // family matching — "We can show it is not available." The KPI fields render a
  // plain "—" and the card shows the "Cost model not available" message
  // (`unavailableMessage` below).
  const isUnavailable = costState.status === "unavailable";
  const costPlaceholder = "—";

  const valueClass = isUnavailable
    ? "text-gray-400"
    : showPrices && calcEnabled && appsValid && calcOk
      ? "text-blue_grey1"
      : "text-blue_grey";

  const totalCostStr =
    showPrices && summary
      ? pickModeCost(machineCostMode, {
          shared: summary.costPerPartShared,
          dedicated: summary.costPerPartDedicated,
          fallback: summary.costPerPart,
        })
      : null;

  const machineCostStr =
    showPrices && summary
      ? pickModeCost(machineCostMode, {
          shared: summary.machineCostPerPartShared,
          dedicated: summary.machineCostPerPartDedicated,
          fallback: summary.machineCostPerPart,
        })
      : null;

  const tabs: Array<{ label: string; kind: "invest" | "install" }> = [];
  if (!hideGlobalTabs) {
    if (showInvestTab) tabs.push({ label: "Lowest Invest", kind: "invest" });
    if (showInstallTab) tabs.push({ label: "Lowest Installation Cost", kind: "install" });
  }

  const unavailableMessage =
    costState.status === "unavailable"
      ? costState.reason === "no_cost_model"
        ? "Cost model not available"
        : costState.reason === "asia_pricing_unavailable"
          ? "Pricing unavailable in ASIA"
          : "Machine price unavailable"
      : costState.status === "error"
        ? costState.message
        : null;

  return (
    <article
      className={`compare-card solutionCard rounded-2xl border border-gray-200 bg-white shadow-soft p-4 flex flex-col h-full relative ${tabs.length ? "hasTabs pt-8" : ""} ${highlightClass}`}
    >
      {tabs.length > 0 && (
        <div className="solutionTabs absolute top-2 left-2 right-2 flex flex-wrap gap-1 z-10">
          {tabs.map((t) => (
            <span
              key={t.label}
              className={`solutionTab text-caption font-medium uppercase px-2 py-0.5 rounded-md ${
                t.kind === "install" ? "install" : "invest"
              }`}
            >
              {t.label}
            </span>
          ))}
        </div>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="absolute top-2 right-2 z-10 w-7 h-7 rounded-full border border-gray-200 bg-white text-blue_grey hover:text-blue_grey1 text-lg leading-none cursor-pointer"
          aria-label={`Remove ${product.name} from comparison`}
        >
          ×
        </button>
      )}

      <div className="flex gap-3 pr-8">
        <div className="min-w-0 flex-1">
          {/* B2 (POC parity): key characteristics moved off the name hover into
              a collapsible section at the card bottom — the name is plain text. */}
          <PennText as="span" size="sm" color="blue_grey1" font="semibold">
            {product.name}
          </PennText>
          {product.subcategory && (
            <PennText as="p" size="xs" color="blue_grey" className="truncate mt-0.5">
              {product.category} · {product.subcategory}
            </PennText>
          )}
        </div>
      </div>

      {(product.imageLink || showMspToggle) && (
        <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50/80 p-3 relative min-h-[140px] flex items-center justify-center">
          {showMspToggle && (
            <div className="absolute left-2 top-2 z-10 rounded-xl border border-gray-200 bg-white/90 shadow-sm px-3 py-2 backdrop-blur-sm">
              <PennText as="p" size="xs" color="blue_grey" font="semibold" className="uppercase">
                MSPe
              </PennText>
              <button
                type="button"
                onClick={onMspAutoFeedToggle}
                className="mt-1 flex items-center gap-2 cursor-pointer"
                aria-pressed={mspAutoFeedOn}
              >
                <PennText as="span" size="xs" color="blue_grey1">
                  Auto-Feed {mspAutoFeedOn ? "On" : "Off"}
                </PennText>
                <span
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    mspAutoFeedOn ? "bg-emerald-500" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                      mspAutoFeedOn ? "translate-x-5" : "translate-x-1"
                    }`}
                  />
                </span>
              </button>
            </div>
          )}
          {product.imageLink && (
            <img
              src={product.imageLink}
              alt={product.name}
              className="max-h-[120px] w-full max-w-[200px] object-contain"
            />
          )}
        </div>
      )}

      <div className={`mt-4 flex-1 space-y-3 relative ${!appsValid ? "pt-8" : ""}`}>
        {/* POC-parity hint (`!inputsValid && selected`): prompt the user to add
            applications so cost + utilization compute. Shown to anonymous
            visitors too (the Installation System filter/compare flow is public) —
            only the actual dollar figures below stay gated behind `showPrices`. */}
        {!appsValid && (
          <div className="kpiHintFloat">
            <span className="kpiHintIcon">i</span>
            <span>Complete application details below to calculate Cost and Utlilization Rates</span>
          </div>
        )}
        {costState.status === "loading" ? (
          <>
            <PennText as="p" size="xs" color="blue_grey" className="flex items-center gap-2">
              <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-gray-300 border-t-penn-500" />
              Calculating…
            </PennText>
            {multiApps ? (
              <div className="space-y-3">
                {selectedApps.map((app) => (
                  <KpiSkeletonBlock
                    key={app.applicationId}
                    showPrices={showPrices}
                    withHeader
                  />
                ))}
              </div>
            ) : (
              <KpiSkeletonBlock showPrices={showPrices} />
            )}
          </>
        ) : (
          <>
        {unavailableMessage && (
          <PennText as="p" size="xs" color="error" font="semibold">
            {unavailableMessage}
          </PennText>
        )}

        {multiApps ? (
          <>
            <div
              className={
                selectedApps.length > 1 ? "compare-apps-scroll space-y-3" : "space-y-3"
              }
            >
              {selectedApps.map((app) => {
                const row =
                  costState.status === "ok"
                    ? costState.data.perApp.find(
                        (r) => r.applicationId === app.applicationId,
                      )
                    : undefined;
                const isLowestForApp =
                  lowestInstallByAppId[app.applicationId] === productId;
                // B6 (POC parity): per-app block is
                // green ("hl-install") when this is the cheapest-to-install
                // machine for the app, amber ("hl-invest") when it's the global
                // lowest-investment machine, blended ("hl-both") when both.
                const isLowestInvestApp = lowestInvestId === productId;
                const blockHighlight =
                  isLowestInvestApp && isLowestForApp
                    ? "hl-both"
                    : isLowestForApp
                      ? "hl-install"
                      : isLowestInvestApp
                        ? "hl-invest"
                        : "";
                return (
                  <PerAppKpiBlock
                    key={app.applicationId}
                    appLabel={
                      app.name?.trim() ||
                      app.applicationNumber?.trim() ||
                      "Application"
                    }
                    row={row}
                    machineCostMode={machineCostMode}
                    currency={currency}
                    showPrices={showPrices}
                    calcEnabled={calcEnabled}
                    appsValid={appsValid}
                    highlightClass={blockHighlight}
                    lowestInvest={isLowestInvestApp}
                    lowestInstall={isLowestForApp}
                    selected={
                      selectedMachineByApp[app.applicationId] === productId
                    }
                    onSelectChange={(checked) =>
                      onToggleMachineForApp(app.applicationId, checked)
                    }
                  />
                );
              })}
            </div>
            {costState.status === "ok" && (
              <SelectedAppsTotalsFooter
                perApp={costState.data.perApp}
                selectedApplicationIds={selectedApps
                  .filter(
                    (a) => selectedMachineByApp[a.applicationId] === productId,
                  )
                  .map((a) => a.applicationId)}
                showValues={showPrices && calcEnabled && appsValid}
              />
            )}
          </>
        ) : (
          <ComparisonKpiBlock
            totalCost={
              showPrices && totalCostStr
                ? formatMoney(totalCostStr, currency)
                : costPlaceholder
            }
            operatorUtil={
              showPrices && summary?.workerUtilization
                ? formatPercentWhole(summary.workerUtilization)
                : "—"
            }
            machineUtil={
              showPrices && summary?.machineUtilization
                ? formatPercentWhole(summary.machineUtilization)
                : "—"
            }
            requiredOperators={
              showPrices && summary ? String(summary.requiredOperators) : "—"
            }
            requiredMachines={
              showPrices && summary ? String(summary.requiredMachines) : "—"
            }
            machineCost={
              showPrices && machineCostStr
                ? formatMoney(machineCostStr, currency)
                : costPlaceholder
            }
            labourCost={
              showPrices && summary
                ? formatMoney(summary.workerCostPerPart, currency)
                : costPlaceholder
            }
            qualityCost={
              showPrices && summary
                ? formatMoney(summary.qualityCostPerPart, currency)
                : costPlaceholder
            }
            valueClass={valueClass}
          />
        )}
          </>
        )}
      </div>

      {/* B2 (POC parity): collapsible Key
          characteristics (up to 5) at the bottom of the card — click to expand.
          Replaces the removed name-hover popover on the comparison card. */}
      {product.keyCharacteristics.length > 0 && (
        <details className="card-kc mt-3 rounded-xl border border-gray-200 bg-gray-50/60">
          <summary className="card-kc-summary cursor-pointer select-none px-3 py-2 flex items-center justify-between gap-2">
            <PennText
              as="span"
              size="xs"
              color="blue_grey"
              font="semibold"
            >
              Key Characteristics
            </PennText>
            <span className="card-kc-chevron text-blue_grey text-xs" aria-hidden="true">
              ▾
            </span>
          </summary>
          <ul className="px-3 pb-3 list-disc pl-7 space-y-1">
            {product.keyCharacteristics.map((kc, i) => (
              <li key={i}>
                <PennText as="span" size="xs" color="blue_grey">
                  {kc}
                </PennText>
              </li>
            ))}
          </ul>
        </details>
      )}

      {onConfigure && (
        <PennButton
          variant="primary"
          size="sm"
          className="w-full mt-4"
          onClick={onConfigure}
        >
          Configure Solution
        </PennButton>
      )}
    </article>
  );
};

type PerAppKpiBlockProps = {
  appLabel: string;
  row: PerAppCostBreakdown | undefined;
  machineCostMode: MachineCostMode;
  currency: string | undefined;
  showPrices: boolean;
  calcEnabled: boolean;
  appsValid: boolean;
  highlightClass: string;
  lowestInvest: boolean;
  lowestInstall: boolean;
  selected: boolean;
  onSelectChange: (checked: boolean) => void;
};

const PerAppKpiBlock = ({
  appLabel,
  row,
  machineCostMode,
  currency,
  showPrices,
  calcEnabled,
  appsValid,
  highlightClass,
  lowestInvest,
  lowestInstall,
  selected,
  onSelectChange,
}: PerAppKpiBlockProps) => {
  const rowOk = row != null && parseDecimalString(row.costPerPart) != null;
  const showNoCostNote = showPrices && calcEnabled && appsValid && !rowOk;
  // MD-04: no synthetic numbers when this application has no cost model for the
  // machine — render "—" and surface the "No cost available" note below.
  const costPlaceholder = "—";
  const valueClass = showNoCostNote
    ? "text-gray-400"
    : showPrices && calcEnabled && appsValid && rowOk
      ? "text-blue_grey1"
      : "text-blue_grey";

  const totalCostStr = rowOk
    ? pickModeCost(machineCostMode, {
        shared: row.costPerPartShared,
        dedicated: row.costPerPartDedicated,
        fallback: row.costPerPart,
      })
    : null;
  const machineCostStr = rowOk
    ? pickModeCost(machineCostMode, {
        shared: row.machineCostPerPartShared,
        dedicated: row.machineCostPerPartDedicated,
        fallback: row.machineCostPerPart,
      })
    : null;

  const header = (
    <>
      <div className="min-w-0">
        <p className="compare-kpi-section-label text-blue_grey">
          Application: {appLabel}
        </p>
        {(lowestInvest || lowestInstall) && (
          <div className="mt-1 flex flex-wrap gap-1">
            {lowestInvest && (
              <span className="solutionTab invest text-caption font-medium uppercase px-2 py-0.5 rounded-md">
                Lowest Invest
              </span>
            )}
            {lowestInstall && (
              <span className="solutionTab install text-caption font-medium uppercase px-2 py-0.5 rounded-md">
                Lowest Installation Cost
              </span>
            )}
          </div>
        )}
      </div>
      <label className="inline-flex items-center gap-2 text-xs text-blue_grey cursor-pointer shrink-0">
        <PennCheckbox
          checked={selected}
          onChange={(e) => onSelectChange(e.target.checked)}
          aria-label={`Select ${appLabel} for this machine`}
        />
        Select for this application
      </label>
    </>
  );

  return (
    <div className="space-y-1">
      {showNoCostNote && (
        <PennText as="p" size="xs" color="error" className="italic">
          No cost available for this application (NA).
        </PennText>
      )}
    <ComparisonKpiBlock
      header={header}
      highlightClass={highlightClass}
      totalCost={
        showPrices && totalCostStr
          ? formatMoney(totalCostStr, currency)
          : costPlaceholder
      }
      operatorUtil={
        showPrices && row?.workerUtilization
          ? formatPercentWhole(row.workerUtilization)
          : "—"
      }
      machineUtil={
        showPrices && row?.machineUtilization
          ? formatPercentWhole(row.machineUtilization)
          : "—"
      }
      requiredOperators={
        showPrices && row
          ? requiredFromUtilization(row.workerUtilization, row.requiredOperators)
          : "—"
      }
      requiredMachines={
        showPrices && row
          ? requiredFromUtilization(row.machineUtilization, row.requiredMachines)
          : "—"
      }
      machineCost={
        showPrices && machineCostStr
          ? formatMoney(machineCostStr, currency)
          : costPlaceholder
      }
      labourCost={
        showPrices && row
          ? formatMoney(row.workerCostPerPart, currency)
          : costPlaceholder
      }
      qualityCost={
        showPrices && row
          ? formatMoney(row.qualityCostPerPart, currency)
          : costPlaceholder
      }
      valueClass={valueClass}
    />
    </div>
  );
};

function formatPercentWhole(value: string | undefined | null): string {
  const n = parseDecimalString(value);
  if (n == null) return "—";
  return `${roundUtilRatioToDisplayPercent(n)}%`;
}

function requiredFromUtilization(
  utilization: string | undefined | null,
  apiRequired: string | undefined,
): string {
  const u = parseDecimalString(utilization);
  if (u != null && u > 0) return String(Math.max(1, Math.ceil(u)));
  return apiRequired ?? "1";
}

/**
 * Shimmer placeholder shown in place of the KPI block while the cost preview
 * is being calculated. Mirrors the real block's grid so the layout doesn't
 * jump when numbers arrive.
 */
const KpiSkeletonBlock = ({
  showPrices,
  withHeader = false,
}: {
  showPrices: boolean;
  withHeader?: boolean;
}) => (
  <div className="costBlock rounded-xl border border-gray-200 bg-gray-50/60 p-3">
    {withHeader && (
      <div className="mb-2 flex items-center justify-between gap-3">
        <Skeleton width="45%" height={12} />
        <Skeleton width={18} height={18} rounded="sm" />
      </div>
    )}
    <div
      className={`grid grid-cols-1 ${showPrices ? "sm:grid-cols-3" : "sm:grid-cols-2"} gap-3`}
    >
      {(showPrices ? [0, 1, 2] : [0, 1]).map((i) => (
        <div key={i} className="flex flex-col items-center gap-2 py-1">
          <Skeleton width="75%" height={9} />
          <Skeleton width="55%" height={20} />
          <Skeleton width="65%" height={8} />
        </div>
      ))}
    </div>
    {showPrices && (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2">
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="rounded-lg border border-gray-200 bg-white/60 px-3 py-2 flex flex-col items-center gap-2"
          >
            <Skeleton width="60%" height={8} />
            <Skeleton width="50%" height={14} />
          </div>
        ))}
      </div>
    )}
  </div>
);

/** POC renderKpiBlock — 3-col centered KPIs + 3 mini cost cards */
type ComparisonKpiBlockProps = {
  header?: ReactNode;
  highlightClass?: string;
  totalCost: string;
  operatorUtil: string;
  machineUtil: string;
  requiredOperators: string;
  requiredMachines: string;
  machineCost: string;
  labourCost: string;
  qualityCost: string;
  valueClass: string;
};

// Structure (columns/rows) always renders — including for anonymous visitors,
// who see the same "—" skeleton as an authenticated user with no application
// yet. Callers already gate the individual VALUES on `showPrices` (real figure
// vs "—"), so this component never needs to know about that permission.
const ComparisonKpiBlock = ({
  header,
  highlightClass = "",
  totalCost,
  operatorUtil,
  machineUtil,
  requiredOperators,
  requiredMachines,
  machineCost,
  labourCost,
  qualityCost,
  valueClass,
}: ComparisonKpiBlockProps) => (
  <div
    className={`costBlock rounded-xl border border-gray-200 bg-gray-50/60 p-3 ${highlightClass}`}
  >
    {header != null && (
      <div className="mb-2 flex items-start justify-between gap-3">{header}</div>
    )}

    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-center">
      <KpiCenterColumn
        label="Total Installation Cost per Part"
        value={totalCost}
        valueClass={valueClass}
        valueSize="total"
      />
      <KpiCenterColumn
        label="Operator utilization"
        value={operatorUtil}
        sub={`Required operators: ${requiredOperators}`}
        valueClass={valueClass}
        valueSize="util"
      />
      <KpiCenterColumn
        label="Machine utilization"
        value={machineUtil}
        sub={`Required machines: ${requiredMachines}`}
        valueClass={valueClass}
        valueSize="util"
      />
    </div>

    <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
      <KpiMiniCentered label="Machine cost" value={machineCost} valueClass={valueClass} />
      <KpiMiniCentered label="Labour cost" value={labourCost} valueClass={valueClass} />
      <KpiMiniCentered label="Quality cost" value={qualityCost} valueClass={valueClass} />
    </div>
  </div>
);

const KpiCenterColumn = ({
  label,
  value,
  sub,
  valueClass,
  valueSize,
}: {
  label: string;
  value: string;
  sub?: string;
  valueClass: string;
  valueSize: "total" | "util";
}) => (
  <div className="flex flex-col items-center min-w-0">
    <div className="compare-kpi-section-label text-blue_grey leading-tight break-words">
      {label}
    </div>
    <div
      className={`mt-1 font-semibold leading-tight ${
        valueSize === "total" ? "compare-kpi-total-value" : "compare-kpi-util-value"
      } ${valueClass}`}
    >
      {value}
    </div>
    {sub != null && (
      <div className="mt-0.5 text-xs text-blue_grey leading-tight break-words">{sub}</div>
    )}
  </div>
);

const KpiMiniCentered = ({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass: string;
}) => (
  <div className="rounded-lg border border-gray-200 bg-white/60 px-3 py-2 flex flex-col items-center text-center min-w-0">
    <div className="compare-kpi-mini-label text-blue_grey whitespace-nowrap">{label}</div>
    <div className={`mt-1 compare-kpi-mini-value ${valueClass} leading-tight break-words`}>
      {value}
    </div>
  </div>
);
